import React, { useEffect, useMemo, useState } from "react";
import "./TestimonialSlider.css";

function Stars({ rating = 0, outOf = 5 }) {
  const r = Math.max(0, Math.min(outOf, Number(rating) || 0));
  const full = "★".repeat(r);
  const empty = "★".repeat(outOf - r);

  return (
    <div className="testimonial-stars" aria-label={`${r} out of ${outOf} stars`}>
      <span aria-hidden="true">{full}</span>
      <span className="testimonial-stars-muted" aria-hidden="true">
        {empty}
      </span>
    </div>
  );
}

export default function TestimonialSlider({ autoRotateMs = 0, perPage = 3 }) {
  const [testimonials, setTestimonials] = useState([]);
  const [page, setPage] = useState(0);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const res = await fetch("/api/reviews/all");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const reviews = (data.reviews || []).filter(
          (r) => (r.quote || "").trim().length > 0
        );

        if (!alive) return;
        setTestimonials(reviews);
        setPage(0);
      } catch (err) {
        console.error("Failed to load reviews:", err);
        if (!alive) return;
        setTestimonials([]);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const pageCount = useMemo(() => {
    if (!testimonials.length) return 0;
    return Math.ceil(testimonials.length / perPage);
  }, [testimonials.length, perPage]);

  const currentItems = useMemo(() => {
    if (!testimonials.length) return [];
    const start = page * perPage;
    return testimonials.slice(start, start + perPage);
  }, [testimonials, page, perPage]);

  const next = () => {
    if (pageCount <= 1) return;
    setPage((p) => (p + 1) % pageCount);
  };

  const prev = () => {
    if (pageCount <= 1) return;
    setPage((p) => (p - 1 + pageCount) % pageCount);
  };

  useEffect(() => {
    if (!autoRotateMs || pageCount <= 1) return;
    const t = setInterval(next, autoRotateMs);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRotateMs, pageCount]);

  if (!testimonials.length) return null;

  return (
    <div className="testimonial-slider">
      <div className="testimonial-grid">
        {currentItems.map((t) => {
          const badgeClass =
            t.source === "Google"
              ? "testimonial-badge badge-google"
              : "testimonial-badge badge-zillow";

          return (
            <div className="testimonial-card" key={t.id}>
              <div className="testimonial-top">
                <div className="testimonial-source">
                  <span className={badgeClass}>{t.source}</span>
                  <Stars rating={t.rating} />
                </div>
              </div>

              <p className="testimonial-quote">“{t.quote}”</p>
              <p className="testimonial-name">{t.name}</p>

              <div className="testimonial-actions">
                <a
                  className="btn small secondary"
                  href={t.viewUrl || "#"}
                  target="_blank"
                  rel="noreferrer"
                >
                  View on {t.source}
                </a>

                <a
                  className="btn small primary"
                  href={t.leaveUrl || t.viewUrl || "#"}
                  target="_blank"
                  rel="noreferrer"
                >
                  Leave a Review
                </a>
              </div>
            </div>
          );
        })}
      </div>

      {pageCount > 1 && (
        <div className="testimonial-footer">
          <div className="testimonial-nav">
            <button
              type="button"
              className="ts-nav-btn"
              onClick={prev}
              aria-label="Previous reviews"
            >
              ‹
            </button>
            <button
              type="button"
              className="ts-nav-btn"
              onClick={next}
              aria-label="Next reviews"
            >
              ›
            </button>
          </div>

          <div className="testimonial-dots" aria-label="Select review page">
            {Array.from({ length: pageCount }).map((_, i) => (
              <button
                key={i}
                type="button"
                className={`ts-dot ${i === page ? "active" : ""}`}
                onClick={() => setPage(i)}
                aria-label={`Go to review page ${i + 1}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
